import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import InventoryAccordionTable from "../components/containers/InventoryAccordionTable";
import KimaruLoader from "../components/common/KimaruLoader";
import InventoryFooter from "../components/InventoryFooter";
import ActionDrawer from "../components/ActionDrawer";
import AgenticInteractionDrawer from "../components/agenticinteraction/AgenticInteractionDrawer";
import ContainerCardAlert from "../components/containers/ContainerCardAlert";

const InventoryOptimizationPage = () => {
    const { containerId } = useParams();
    const userEmail = useSelector((state) => state.auth?.user?.email);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Remove isActionDrawerOpen state
    const [isAgenticDrawerOpen, setIsAgenticDrawerOpen] = useState(false);
    const [currentRecommendation, setCurrentRecommendation] = useState(null);
    // Hold optional containerId and scope when opening the agent for SKU-level actions
    const [currentAgentContainerId, setCurrentAgentContainerId] = useState(null);
    const [currentAgentScope, setCurrentAgentScope] = useState(null);
    const [overriddenRecommendations, setOverriddenRecommendations] = useState(new Set());

    useEffect(() => {
        const fetchRecommendations = async () => {
            try {
                setLoading(true);

                let movementResponse, priceResponse;
                if (userEmail === "sam@kimaru.ai") {
                    // Fetch only movement recommendations
                    movementResponse = await fetch(
                        "https://api.stag.kimaru.ai/api/v1/movement_recommendation/get_recommendation",
                        {
                            method: "GET",
                            headers: {
                                accept: "application/json",
                                "user-email": userEmail
                            },
                        }
                    );

                    if (!movementResponse.ok) {
                        throw new Error(`Movement API error! status: ${movementResponse.status}`);
                    }
                } else {
                    // Fetch both movement and price recommendations
                    [movementResponse, priceResponse] = await Promise.all([
                        fetch("https://api.stag.kimaru.ai/api/v1/movement_recommendation/get_recommendation", {
                            method: "GET",
                            headers: {
                                accept: "application/json",
                                "user-email": userEmail
                            },
                        }),
                        fetch("https://api.stag.kimaru.ai/api/v1/price_recommendation/get_recommendation", {
                            method: "GET",
                            headers: {
                                accept: "application/json",
                                "user-email": userEmail
                            },
                        })
                    ]);

                    if (!movementResponse.ok) throw new Error(`Movement API error! status: ${movementResponse.status}`);
                    if (!priceResponse.ok) throw new Error(`Price API error! status: ${priceResponse.status}`);
                }

                const movementData = await movementResponse.json();
                const priceData = userEmail === "sam@kimaru.ai" ? null : await priceResponse.json();

                const mergedData = {
                    containers: {}
                };

                // Merge movement recommendations
                if (movementData.containers) {
                    Object.keys(movementData.containers).forEach(containerId => {
                        mergedData.containers[containerId] = {
                            ...movementData.containers[containerId],
                            movementRecommendations: movementData.containers[containerId].recommendations || []
                        };
                    });
                }

                // Merge price recommendations (only if not Sam)
                if (priceData && priceData.containers) {
                    Object.keys(priceData.containers).forEach(containerId => {
                        if (!mergedData.containers[containerId]) {
                            mergedData.containers[containerId] = {
                                container_id: containerId,
                                location: priceData.containers[containerId].location,
                                movementRecommendations: [],
                                priceRecommendations: []
                            };
                        }
                        mergedData.containers[containerId].priceRecommendations =
                            priceData.containers[containerId].recommendations || [];
                    });
                }

                // Combine recommendations per container
                Object.keys(mergedData.containers).forEach(containerId => {
                    const container = mergedData.containers[containerId];
                    container.recommendations = [
                        ...(container.movementRecommendations.map(rec => ({
                            ...rec,
                            recommendation_type: 'movement'
                        }))),
                        ...((!priceData || !container.priceRecommendations) ? [] :
                            container.priceRecommendations.map(rec => ({
                                ...rec,
                                recommendation_type: 'price'
                            })))
                    ];
                });

                setData(mergedData);

                if (mergedData.containers[containerId]) {
                    const containerData = mergedData.containers[containerId];
                    const movementItems = containerData.movementRecommendations || [];

                    // Calculate total units only from movement recommendations
                    const totalUnits = movementItems.reduce((sum, item) => {
                        return sum + (item.quantity_on_hand || 0);
                    }, 0);

                    const capacityPercentage = Math.round((totalUnits / 3500) * 100);
                    const today = new Date();
                    const monthNames = [
                        "January", "February", "March", "April", "May", "June",
                        "July", "August", "September", "October", "November", "December"
                    ];
                    const formattedDate = `${monthNames[today.getMonth()]} ${today.getDate()}`;

                    // Filter only movement items for low stock calculation
                    const lowStockItems = [...movementItems]
                        .filter(item => item.quantity_on_hand !== undefined)
                        .sort((a, b) => a.quantity_on_hand - b.quantity_on_hand)
                        .slice(0, 3)
                        .map(item => ({
                            name: item.item_name,
                            quantity: item.quantity_on_hand,
                            percentage: Math.round((item.quantity_on_hand / 35) * 100),
                            type: 'movement'  // Only movement items have stock data
                        }));

                    containerData.specialData = {
                        totalUnits,
                        lastRestocked: formattedDate,
                        capacityPercentage,
                        lowStockItems
                    };

                    const isHeatWarningLocation = ["Miyazaki", "Osaka"].includes(containerData.location);
                    containerData.type = isHeatWarningLocation ? "heatwave" : "spoilage";
                    containerData.itemsToReview = containerData.recommendations.length;
                }
            } catch (err) {
                setError(err.message);
                console.error("Fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchRecommendations();
    }, [userEmail, containerId]);

    // Removed duplicate handleFinishClick
    const navigate = useNavigate();
    const handleFinishClick = () => {
        navigate(`/action-drawer/${containerId}`);
    };
    // const handleCloseActionDrawer = () => setIsActionDrawerOpen(false);
    const handleOpenAgenticDrawer = (recommendationOrIds, containerIdForAgent = null, agentScope = null) => {
        // recommendationOrIds can be:
        // - a recommendation object (single rec)
        // - an array of recommendation ids (from InventoryAccordionTable SKU button)
        // - null/undefined (not expected)
        setCurrentRecommendation(recommendationOrIds);
        setCurrentAgentContainerId(containerIdForAgent || null);
        setCurrentAgentScope(agentScope || null);
        setIsAgenticDrawerOpen(true);
    };

    const handleMarkOverridden = (recommendationIdOrIds) => {
        try {
            setOverriddenRecommendations(prev => {
                const next = new Set(prev);
                if (Array.isArray(recommendationIdOrIds)) {
                    recommendationIdOrIds.forEach(id => id && next.add(id));
                } else if (recommendationIdOrIds) {
                    next.add(recommendationIdOrIds);
                }
                return next;
            });
        } catch (err) {
            console.error('Failed to mark overridden recommendation(s)', err);
        }
    };

    const handleUnmarkOverridden = (recommendationIdOrIds) => {
        try {
            setOverriddenRecommendations(prev => {
                const next = new Set(prev);
                if (Array.isArray(recommendationIdOrIds)) {
                    recommendationIdOrIds.forEach(id => next.delete(id));
                } else if (recommendationIdOrIds) {
                    next.delete(recommendationIdOrIds);
                }
                return next;
            });
        } catch (err) {
            console.error('Failed to unmark overridden recommendation(s)', err);
        }
    };

    const handleCloseAgenticDrawer = () => {
        setIsAgenticDrawerOpen(false);
        setCurrentRecommendation(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <KimaruLoader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-red-500">
                Error: {error}
            </div>
        );
    }

    const container = data?.containers?.[containerId];
    if (!container) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-gray-500">
                No data found for container ID: {containerId}
            </div>
        );
    }

    const currentStep = 1;

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            {/* Inventory footer moved to top */}
            <InventoryFooter
                isLoading={loading}
                currentStep={currentStep}
                onFinishClick={handleFinishClick}
            />

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
                <div className="max-w-full mx-auto">
                    <ContainerCardAlert
                        key={containerId}
                        container={container}
                        type={container.type || "sell-through"}
                        buttonDisabled={false}
                        containerData={container}
                        onInventoryOptimizationPage={true}
                    />
                    <InventoryAccordionTable
                        containerData={container}
                        onOpenAgenticDrawer={handleOpenAgenticDrawer}
                        overriddenRecommendations={overriddenRecommendations}
                        onMarkOverridden={handleMarkOverridden}
                        onUnmarkOverridden={handleUnmarkOverridden}
                    />
                </div>
            </div>

            {/* ActionDrawer removed: now navigates to ActionDrawerPage */}

            <AgenticInteractionDrawer
                open={isAgenticDrawerOpen}
                onClose={handleCloseAgenticDrawer}
                // If currentRecommendation is an array (ids), pass it directly.
                // Otherwise, try to extract the nested recommendation id from the object.
                recommendationId={Array.isArray(currentRecommendation)
                    ? currentRecommendation
                    : (currentRecommendation?.recommendation?.id || currentRecommendation?.rec_id || null)}
                recommendation={Array.isArray(currentRecommendation) ? null : currentRecommendation}
                containerId={currentAgentContainerId}
                agentScope={currentAgentScope}
                onAcceptSuccess={(ids) => {
                    // ids can be array or single id
                    handleMarkOverridden(ids);
                }}
            />
        </div>
    );
};

export default InventoryOptimizationPage;